import React, { useState, useEffect, useCallback } from "react";
import { Drawer } from "@mui/material";
import { CloseIcon } from "../../Components/Icons/Miscellaneous/CloseIcon";
import { Button } from "../Button/Button";
import { Checkbox } from "../Checkbox/Checkbox";
import { Accordion } from "../Accordion/Accordion";
import { Book2Icon } from "../../Components/Icons/Documents/Book2Icon";
import { BooksIcon } from "../../Components/Icons/Documents/BooksIcon";
import { MessageChatbotIcon } from "../../Components/Icons/Messaging/MessageChatbotIcon";
import { AntennaBars5Icon } from "../../Components/Icons/Messaging/AntennaBars5Icon";
import "./FilterDrawer.scss";
 
const FILTER_ICONS = {
  tipos: <Book2Icon className="text-azul" />,
  temas: <BooksIcon className="text-azul" />,
  idiomas: <MessageChatbotIcon className="text-azul" />,
  dificultad: <AntennaBars5Icon className="text-azul" />,
};
 
const FILTER_LABELS = {
  tipos: "Tipo",
  temas: "Temas",
  idiomas: "Idioma",
  dificultad: "Dificultad",
};
 
/**
* @param {boolean} open - Controla si el drawer está abierto o cerrado
* @param {object} savedFilters - Filtros guardados desde el estado global
* @param {object} filterData - Datos de filtros desde endpoints
* @param {object} tempFilters - Filtros temporales activos
* @param {function} onTempFilterChange - Handler para cambios temporales
* @param {function} onApplyFilters - Handler para aplicar filtros finales
* @param {function} onClearFilters - Handler para limpiar filtros
* @param {function} onClose - Función que se ejecuta al cerrar el drawer
*/
export const FilterDrawer = ({
  open,
  savedFilters,
  filterData,
  tempFilters,
  onTempFilterChange,
  onApplyFilters,
  onClearFilters,
  onClose,
}) => {
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
 
  const filterItems = Object.keys(filterData).map((key) => ({
    id: key,
    label: FILTER_LABELS[key],
    iconComponent: FILTER_ICONS[key],
    title: `Selecciona ${FILTER_LABELS[key].toLowerCase()}:`,
    data: filterData[key],
  }));
 
  const handleItemClick = (categoryId) => {
    const filterItem = filterItems.find((item) => item.id === categoryId);
    setSelectedFilter(filterItem);
    setBottomSheetOpen(true);
  };
 
  const handleBottomSheetClose = () => {
    setBottomSheetOpen(false);
    setSelectedFilter(null);
  };
 
  useEffect(() => {
    if (!open) {
      setSelectedFilter(null);
      setBottomSheetOpen(false);
    }
  }, [open]);
 
  const getSelectedFilterNames = (categoryId) => {
    const selectedIds = savedFilters[categoryId] || [];
    const categoryData = filterData[categoryId] || [];
 
    if (categoryId === "temas") {
      const selectedNames = [];
      categoryData.forEach((categoria) => {
        categoria.subcategorias?.forEach((sub) => {
          if (selectedIds.includes(sub.idSubcategoria)) {
            selectedNames.push(sub.subcategoria);
          }
        });
      });
      return selectedNames.join(", ");
    }
 
    return selectedIds
      .map((id) => {
        const option = categoryData.find(
          (item) => getOptionId(item, categoryId) === id
        );
        return option?.nombre;
      })
      .filter(Boolean)
      .join(", ");
  };
 
  const getOptionId = (option, categoryId) => {
    switch (categoryId) {
      case "tipos":
        return option.idTipoAprendizaje;
      case "temas":
        return option.idCategoria;
      case "idiomas":
        return option.idIdioma;
      case "dificultad":
        return option.idDificultad;
      default:
        return option.id;
    }
  };
 
  const isOptionSelected = useCallback(
    (optionId, categoryId) => {
      return tempFilters[categoryId]?.includes(optionId) || false;
    },
    [tempFilters]
  );
 
  const handleCheckboxChange = useCallback(
    (categoryId, optionId, checked) => {
      const isSingleSelection = categoryId === "tipos" && checked;
      onTempFilterChange?.(categoryId, optionId, checked, isSingleSelection);
    },
    [onTempFilterChange]
  );
 
  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        hideBackdrop
        className="filter-drawer"
        ModalProps={{
          disableEnforceFocus: true,
        }}
      >
        <div className="filter-drawer__header">
          <h2 className="heading-m-bold mb-0">Buscar por:</h2>
          <button className="filter-drawer__close-button" onClick={onClose}>
            <CloseIcon width={32} height={30} className="text-azul-7" />
          </button>
        </div>
        <div className="filter-drawer__separator"></div>
 
        <div className="filter-drawer__content">
          {filterItems.map((item) => (
            <div
              key={item.id}
              className="filter-drawer__item"
              onClick={() => handleItemClick(item.id)}
            >
              <div className="filter-drawer__info">
                {item.iconComponent}
                <span className="filter-drawer__label">{item.label} </span>
              </div>
              <div className="filter-drawer__value">
                {getSelectedFilterNames(item.id)}
              </div>
            </div>
          ))}
        </div>
 
        <div className="filter-drawer__actions">
          <Button size="medium" variant="secundario" onClick={onClearFilters}>
            Limpiar
          </Button>
          <Button size="medium" onClick={onApplyFilters}>
            Aplicar
          </Button>
        </div>
      </Drawer>
 
      <Drawer
        anchor="bottom"
        open={bottomSheetOpen}
        onClose={handleBottomSheetClose}
        className="filter-bottom-sheet"
        ModalProps={{
          disableEnforceFocus: true,
        }}
      >
        <div className="filter-bottom-sheet__content">
          {selectedFilter && (
            <>
              <h3 className="paragraph-l-bold mb-0">{selectedFilter.title}</h3>
              <div className="filter-bottom-sheet__separator"></div>
 
              <div className="filter-bottom-sheet__options">
                {selectedFilter.id === "temas" && (
                  <>
                    {selectedFilter.data
                      .filter(
                        (item) =>
                          Array.isArray(item.subcategorias) &&
                          item.subcategorias.length > 0
                      )
                      .map((categoria) => (
                        <Accordion
                          key={categoria.idCategoria}
                          title={categoria.nombre}
                          titleClassName="paragraph-l-regular"
                          className="filter-bottom-sheet__temas"
                        >
                          {categoria.subcategorias
                            .filter((item) => Boolean(item.estado))
                            .map((subcategoria) => (
                              <div
                                key={subcategoria.idSubcategoria}
                                className="filter-bottom-sheet__options-checkbox"
                              >
                                <Checkbox
                                  id={subcategoria.idSubcategoria}
                                  checked={isOptionSelected(
                                    subcategoria.idSubcategoria,
                                    "temas"
                                  )}
                                  onChange={(e) =>
                                    handleCheckboxChange(
                                      "temas",
                                      subcategoria.idSubcategoria,
                                      e.target.checked
                                    )
                                  }
                                  className="paragraph-l-regular"
                                >
                                  {subcategoria.subcategoria}
                                </Checkbox>
                              </div>
                            ))}
                        </Accordion>
                      ))}
                  </>
                )}
 
                {selectedFilter.id !== "temas" && (
                  <>
                    {selectedFilter.data.map((option) => {
                      const optionId = getOptionId(option, selectedFilter.id);
                      const optionLabel = option.nombre;
 
                      return (
                        <div
                          key={optionId}
                          className="filter-bottom-sheet__options-checkbox"
                        >
                          <Checkbox
                            id={optionId}
                            checked={isOptionSelected(
                              optionId,
                              selectedFilter.id
                            )}
                            onChange={(e) =>
                              handleCheckboxChange(
                                selectedFilter.id,
                                optionId,
                                e.target.checked
                              )
                            }
                            className="paragraph-l-regular"
                          >
                            {optionLabel}
                          </Checkbox>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
 
              <div className="filter-bottom-sheet__actions">
                <Button
                  size="medium"
                  variant="secundario-cta"
                  onClick={handleBottomSheetClose}
                >
                  Cancelar
                </Button>
                <Button size="medium" onClick={onApplyFilters}>
                  Aplicar
                </Button>
              </div>
            </>
          )}
        </div>
      </Drawer>
    </>
  );
};